import React, { useEffect, useState, createContext, useContext } from 'react';
import { Lead, Interaction } from '../types/lead';
import { CalendarEvent } from '../types/calendar';
const STORAGE_KEY = 'growthly_data_v1';
interface QaRecord {
  id: string;
  leadId: string;
  date: string;
  score: number;
  notes: string;
}
interface DataStoreState {
  leads: Lead[];
  events: CalendarEvent[];
  timeline: Interaction[];
  qaRecords: QaRecord[];
}
const initialState: DataStoreState = {
  leads: [],
  events: [],
  timeline: [],
  qaRecords: []
};
interface DataStoreContextType extends DataStoreState {
  createLead: (lead: Partial<Lead>) => Lead;
  updateLead: (id: string, patch: Partial<Lead>) => void;
  deleteLead: (id: string) => void;
  createEvent: (event: Omit<CalendarEvent, 'id'>) => void;
  updateEvent: (id: string, patch: Partial<CalendarEvent>) => void;
  deleteEvent: (id: string) => void;
  addTimelineEvent: (event: Omit<Interaction, 'id'>) => void;
  createQaRecord: (record: Omit<QaRecord, 'id'>) => void;
  resetAll: () => void;
}
const DataStoreContext = createContext<DataStoreContextType | undefined>(undefined);
export function DataStoreProvider({
  children
}: {
  children: ReactNode;
}) {
  const [data, setData] = useState<DataStoreState>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : initialState;
    } catch {
      return initialState;
    }
  });
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);
  const createLead = (lead: Partial<Lead>): Lead => {
    const newLead: Lead = {
      id: `L-${Date.now()}`,
      NOMBRE_NEGOCIO: lead.NOMBRE_NEGOCIO || '',
      DECISOR_NOMBRE: lead.DECISOR_NOMBRE || '',
      DECISOR_CARGO: lead.DECISOR_CARGO || '',
      TELEFONO_WHATSAPP: lead.TELEFONO_WHATSAPP || '',
      VERIFICADO: lead.VERIFICADO || false,
      DIRECCION: lead.DIRECCION || {
        calle: '',
        numero: '',
        barrio: '',
        ciudad: ''
      },
      TAG_GEO: lead.TAG_GEO || '',
      RRSS_HANDLES: lead.RRSS_HANDLES || {},
      ESTADO_WEB_MENU: lead.ESTADO_WEB_MENU || 'No web',
      MENU_STATUS: lead.MENU_STATUS || 'Papel',
      NOTA_CARENCIA: lead.NOTA_CARENCIA || '',
      EVIDENCE_ATTACHMENTS: lead.EVIDENCE_ATTACHMENTS || [],
      LEAD_SCORE: lead.LEAD_SCORE || 0,
      ICP_PRIORITY: lead.ICP_PRIORITY || 'C',
      LEAD_SOURCE: lead.LEAD_SOURCE || 'Otro',
      ENRICHMENT_READY: lead.ENRICHMENT_READY || false,
      OWNER_ID: lead.OWNER_ID || 'IVANA',
      STATE: lead.STATE || 'Nuevo',
      EVIDENCE_COMPLETENESS: lead.EVIDENCE_COMPLETENESS || 0,
      CREATED_AT: new Date().toISOString(),
      UPDATED_AT: new Date().toISOString(),
      ...lead
    };
    setData(prev => ({
      ...prev,
      leads: [newLead, ...prev.leads]
    }));
    return newLead;
  };
  const updateLead = (id: string, patch: Partial<Lead>) => {
    setData(prev => ({
      ...prev,
      leads: prev.leads.map(l => l.id === id ? {
        ...l,
        ...patch,
        UPDATED_AT: new Date().toISOString()
      } : l)
    }));
  };
  const deleteLead = (id: string) => {
    setData(prev => ({
      ...prev,
      leads: prev.leads.filter(l => l.id !== id),
      timeline: prev.timeline.filter(t => t.leadId !== id),
      events: prev.events.filter(e => e.leadId !== id),
      qaRecords: prev.qaRecords.filter(q => q.leadId !== id)
    }));
  };
  const createEvent = (event: Omit<CalendarEvent, 'id'>) => {
    const newEvent: CalendarEvent = {
      ...event,
      id: `E-${Date.now()}`
    };
    setData(prev => ({
      ...prev,
      events: [...prev.events, newEvent]
    }));
  };
  const updateEvent = (id: string, patch: Partial<CalendarEvent>) => {
    setData(prev => ({
      ...prev,
      events: prev.events.map(e => e.id === id ? {
        ...e,
        ...patch
      } : e)
    }));
  };
  const deleteEvent = (id: string) => {
    setData(prev => ({
      ...prev,
      events: prev.events.filter(e => e.id !== id)
    }));
  };
  const addTimelineEvent = (event: Omit<Interaction, 'id'>) => {
    const newEvent: Interaction = {
      ...event,
      id: `T-${Date.now()}`
    };
    setData(prev => ({
      ...prev,
      timeline: [newEvent, ...prev.timeline]
    }));
  };
  const createQaRecord = (record: Omit<QaRecord, 'id'>) => {
    const newRecord: QaRecord = {
      ...record,
      id: `Q-${Date.now()}`
    };
    setData(prev => ({
      ...prev,
      qaRecords: [newRecord, ...prev.qaRecords]
    }));
  };
  const resetAll = () => {
    setData(initialState);
  };
  return <DataStoreContext.Provider value={{
    ...data,
    createLead,
    updateLead,
    deleteLead,
    createEvent,
    updateEvent,
    deleteEvent,
    addTimelineEvent,
    createQaRecord,
    resetAll
  }} data-id="element-776">
      {children}
    </DataStoreContext.Provider>;
}
export const useDataStore = () => {
  const ctx = useContext(DataStoreContext);
  if (!ctx) throw new Error('useDataStore must be used within DataStoreProvider');
  return ctx;
};